import {
  DEMOGRAPHICS_URL,
  DEMOGRAPHICS_ETHNICITIES_URL,
  DEMOGRAPHICS_GENDERS_URL,
  DEMOGRAPHICS_PATCH_URL,
} from './constants';
import useAxios from '../hooks/useAxios';

/**
 * Hook for interacting with the Questionnaire API.
 *
 * @returns {Object} An object containing functions to interact with the API.
 */
export const useQuestionnaireApi = () => {
  const api = useAxios();

  /**
   * Posts demographics form questionnaire response to the server.
   *
   * @param {string} dateOfBirth - The user's date of birth.
   * @param {number} genderId - The ID of the selected gender.
   * @param {number} ethnicityId - The ID of the selected ethnicity.
   * @param {number} height - The user's height.
   * @param {number} weight - The user's weight.
   * @param {boolean} isFrontendHeightImperialUnits - Whether the height is in imperial units.
   * @param {boolean} isFrontendWeightImperialUnits - Whether the weight is in imperial units.
   * @param {boolean} isSmoker - Whether the user is a smoker.
   * @param {number} diabetesTypeId - The ID of the selected diabetes type.
   * @param {boolean} isHighBP - Whether the user has high blood pressure.
   * @param {boolean} isOnBPMeds - Whether the user is on blood pressure medications.
   * @param {boolean} pregnant - Whether the user is pregnant.
   * @param {boolean} hasHadStroke - Whether the user has had a stroke.
   * @param {boolean} hasHadHeartAttack - Whether the user has had a heart attack.
   * @returns {Promise} A Promise that resolves when the request is complete.
   */
  const postQuestionnaire = (
    dateOfBirth,
    genderId,
    ethnicityId,
    height,
    weight,
    isFrontendHeightImperialUnits,
    isFrontendWeightImperialUnits,
    isSmoker,
    diabetesTypeId,
    isHighBP,
    isOnBPMeds,
    pregnant,
    hasHadStroke,
    hasHadHeartAttack,
    menopauseStatus,
  ) =>
    api.post(DEMOGRAPHICS_URL, {
      date_of_birth: dateOfBirth,
      gender_id: genderId,
      ethnicity_id: ethnicityId,
      height: height,
      weight: weight,
      is_frontend_height_imperial_units: isFrontendHeightImperialUnits,
      is_frontend_weight_imperial_units: isFrontendWeightImperialUnits,
      is_smoker: isSmoker,
      diabetes_type_id: diabetesTypeId,
      has_high_bp: isHighBP,
      is_on_bp_meds: isOnBPMeds,
      has_had_heart_attack: hasHadHeartAttack,
      has_had_stroke: hasHadStroke,
      pregnant: pregnant,
      menopausal_status: menopauseStatus,
    });

  /**
   * Patch demographics form questionnaire response to the server.
   *
   * @param {string} dateOfBirth - The user's date of birth.
   * @param {number} genderId - The ID of the selected gender.
   * @param {number} ethnicityId - The ID of the selected ethnicity.
   * @param {number} height - The user's height.
   * @param {number} weight - The user's weight.
   * @param {boolean} isFrontendHeightImperialUnits - Whether the height is in imperial units.
   * @param {boolean} isFrontendWeightImperialUnits - Whether the weight is in imperial units.
   * @param {boolean} isSmoker - Whether the user is a smoker.
   * @param {number} diabetesTypeId - The ID of the selected diabetes type.
   * @param {boolean} isHighBP - Whether the user has high blood pressure.
   * @param {boolean} isOnBPMeds - Whether the user is on blood pressure medications.
   * @param {boolean} pregnant - Whether the user is pregnant.
   * @param {boolean} hasHadStroke - Whether the user has had a stroke.
   * @param {boolean} hasHadHeartAttack - Whether the user has had a heart attack.
   * @returns {Promise} A Promise that resolves when the request is complete.
   */
  // const patchQuestionnaire = (
  //   dateOfBirth,
  //   genderId,
  //   ethnicityId,
  //   height,
  //   weight,
  //   isFrontendHeightImperialUnits,
  //   isFrontendWeightImperialUnits,
  //   isSmoker,
  //   diabetesTypeId,
  //   isHighBP,
  //   isOnBPMeds,
  //   pregnant,
  //   hasHadStroke,
  //   hasHadHeartAttack,
  // ) =>
  //   api.patch(DEMOGRAPHICS_PATCH_URL , {
  //     date_of_birth: dateOfBirth,
  //     gender_id: genderId,
  //     ethnicity_id: ethnicityId,
  //     height: height,
  //     weight: weight,
  //     is_frontend_height_imperial_units: isFrontendHeightImperialUnits,
  //     is_frontend_weight_imperial_units: isFrontendWeightImperialUnits,
  //     is_smoker: isSmoker,
  //     diabetes_type_id: diabetesTypeId,
  //     has_high_bp: isHighBP,
  //     is_on_bp_meds: isOnBPMeds,
  //     has_had_heart_attack: hasHadHeartAttack,
  //     has_had_stroke: hasHadStroke,
  //     pregnant: pregnant,
  //   });

  const patchQuestionnaire = (
    dateOfBirth,
    genderId,
    ethnicityId,
    height,
    weight,
    isFrontendHeightImperialUnits,
    isFrontendWeightImperialUnits,
    isSmoker,
    diabetesTypeId,
    isHighBP,
    isOnBPMeds,
    pregnant,
    menopauseStatus,
    hasHadHeartAttack,
    hasHadStroke,
    wellnessGoals,
    hasMedicalCondition,
    medicalConditionNames,
    hasAllergies,
    allergyNames,
    hasSupplements, // if not taking supplements, pass null
    supplements, // if not taking supplements, pass null
  ) => {
    // Ensure that if the user does not have supplements or allergies, their names are set to null
    const data = {
      date_of_birth: dateOfBirth,
      gender_id: genderId,
      ethnicity_id: ethnicityId,
      height: height,
      weight: weight,
      is_frontend_height_imperial_units: isFrontendHeightImperialUnits,
      is_frontend_weight_imperial_units: isFrontendWeightImperialUnits,
      is_smoker: isSmoker,
      diabetes_type_id: diabetesTypeId,
      has_high_bp: isHighBP,
      is_on_bp_meds: isOnBPMeds,
      has_had_heart_attack: hasHadHeartAttack,
      has_had_stroke: hasHadStroke,
      pregnant: pregnant,
      menopausal_status: menopauseStatus,
      wellness_goals: wellnessGoals,
      has_medical_condition: hasMedicalCondition,
      medical_condition_name: medicalConditionNames,
      has_allergies: hasAllergies,
      allergy_name: hasAllergies ? allergyNames : null, // Only include allergy names if allergies exist
      has_supplement: hasSupplements,
      supplement_name: hasSupplements ? supplements : null, // Only include supplement names if supplements exist
    };
    console.log('Patch Questionnaire Data:', menopauseStatus);

    return api.patch(DEMOGRAPHICS_PATCH_URL, data);
  };

  /**
   * Gets the user's demographics form questionnaire data response.
   *
   * @returns {Promise} A Promise that resolves with the user's questionnaire response.
   */
  const getQuestionnaire = () => api.get(DEMOGRAPHICS_URL);

  /**
   * Gets the list of available ethnicities.
   *
   * @returns {Promise} A Promise that resolves with the list of ethnicities.
   */
  const getEthnicities = () => api.get(DEMOGRAPHICS_ETHNICITIES_URL);

  /**
   * Gets the list of available genders.
   *
   * @returns {Promise} A Promise that resolves with the list of genders.
   */
  const getGenders = () => api.get(DEMOGRAPHICS_GENDERS_URL);

  return {
    postQuestionnaire,
    getQuestionnaire,
    getEthnicities,
    getGenders,
    patchQuestionnaire,
  };
};
